import React, { useState } from 'react';
import Button from '../../../ui/Button';
import Card from '../../../ui/Card';
import { formatBytes, getSourceName } from '../utils';
import axiosInstance from '../../../api/axiosInstance';

export default function ReviewStep({ state, onStarted, showNotification }) {
  const [starting, setStarting] = useState(false);

  const { source, method, credentials, selectedUsers, selectedUserDetails, domainMapping, csvUsers, validationResult } = state;

  const userCount = method === 'bulk' ? csvUsers?.length : selectedUsers?.length;
  const totalSize = validationResult?.totalSize || 0;

  const handleStart = async () => {
    setStarting(true);
    try {
      let response;

      if (method === 'bulk') {
        response = await axiosInstance.post('/api/business/email-migration/bulk-start', {
          sourceType: source?.id,
          migrationType: 'bulk',
          csvUsers: csvUsers.filter(u => u._valid !== false),
          totalUsers: csvUsers.filter(u => u._valid !== false).length
        });
      } else {
        response = await axiosInstance.post('/api/business/email-migration/start', {
          sourceType: source?.id,
          credentials,
          domainMapping,
          selectedUsers,
          selectedUserDetails
        });
      }

      if (response.data.success) {
        onStarted(response.data.migrationId);
      } else {
        showNotification?.(response.data.error || 'Failed to start migration', 'error');
      }
    } catch (err) {
      showNotification?.(err.response?.data?.error || err.message || 'Failed to start migration', 'error');
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Review & Start Migration</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Review the migration configuration before starting
        </p>
      </div>

      <Card padding="md">
        <div className="space-y-4">
          {/* Source */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500">Source Provider</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{getSourceName(source?.id)}</span>
          </div>

          {/* Method */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500">Migration Type</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">{method}</span>
          </div>

          {/* Users */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="text-sm text-gray-500">Users to Migrate</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">{userCount}</span>
          </div>

          {/* Total Size */}
          {totalSize > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
              <span className="text-sm text-gray-500">Estimated Total Size</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{formatBytes(totalSize)}</span>
            </div>
          )}

          {/* Domain Mappings */}
          {domainMapping && Object.keys(domainMapping).length > 0 && (
            <div className="py-2 border-b border-gray-100 dark:border-gray-800">
              <span className="text-sm text-gray-500 block mb-2">Domain Mappings</span>
              {Object.entries(domainMapping).map(([from, to]) => (
                <div key={from} className="flex items-center gap-2 text-sm pl-2 mb-1">
                  <span className="text-gray-700 dark:text-gray-300">{from}</span>
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                  <span className="font-medium text-gray-900 dark:text-white">{typeof to === 'object' ? to.name : to}</span>
                </div>
              ))}
            </div>
          )}

          {/* Connection Info */}
          {credentials?.accessToken ? (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-500">Connected via</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                OAuth ({credentials.adminEmail})
              </span>
            </div>
          ) : credentials?.hostname && (
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-gray-500">Source Server</span>
              <span className="text-sm font-mono text-gray-700 dark:text-gray-300">{credentials.hostname}:{credentials.port || 993}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Warning */}
      <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="text-sm text-amber-700 dark:text-amber-300">
          <p className="font-medium">Before you start</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Ensure the target domain is configured on your mail server</li>
            <li>Migration may take time depending on mailbox sizes</li>
            <li>You can monitor progress in real-time after starting</li>
          </ul>
        </div>
      </div>

      {/* Start Button */}
      <Button
        variant="primary"
        size="lg"
        fullWidth
        onClick={handleStart}
        loading={starting}
        disabled={starting}
      >
        {starting ? 'Starting Migration...' : `Start Migration (${userCount} user${userCount !== 1 ? 's' : ''})`}
      </Button>
    </div>
  );
}
